import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ExternalLink, FileText, RefreshCw } from 'lucide-react';
import { Spinner } from '../common/Spinner';
import type { AnalyzedPaper } from '../../api/client';

interface PapersTableProps {
  papers: AnalyzedPaper[];
  benchmark: string;
}

export const PapersTable = ({ papers, benchmark }: PapersTableProps) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'year' | 'citation_count'>('citation_count');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const sortedPapers = [...papers].sort((a, b) => {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    return (a[sortField] - b[sortField]) * multiplier;
  });

  const toggleSort = (field: 'year' | 'citation_count') => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const toggleExpand = (paperId: string) => {
    setExpandedRow(expandedRow === paperId ? null : paperId);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">论文标题</th>
              <th
                className="px-4 py-3 text-center text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleSort('year')}
              >
                年份 {sortField === 'year' && (sortOrder === 'desc' ? '↓' : '↑')}
              </th>
              <th
                className="px-4 py-3 text-center text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleSort('citation_count')}
              >
                引用数 {sortField === 'citation_count' && (sortOrder === 'desc' ? '↓' : '↑')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">方法</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">准确率</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-16">PDF</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedPapers.map((paper, index) => (
              <PaperRow
                key={paper.paper_id}
                paper={paper}
                benchmark={benchmark}
                isExpanded={expandedRow === paper.paper_id}
                onToggle={() => toggleExpand(paper.paper_id)}
                index={index}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 单行组件
interface PaperRowProps {
  paper: AnalyzedPaper;
  benchmark: string;
  isExpanded: boolean;
  onToggle: () => void;
  index: number;
}

const PaperRow = ({ paper, benchmark, isExpanded, onToggle, index }: PaperRowProps) => {
  // 分析状态判断
  const isPending = paper.analysis_status === 'pending';
  const isAnalyzingSimple = paper.analysis_status === 'analyzing_simple';
  const isSimpleCompleted = paper.analysis_status === 'simple_completed';
  const isAnalyzingFulltext = paper.analysis_status === 'analyzing_fulltext';
  const isCompleted = paper.analysis_status === 'completed';
  const isSimpleOnly = paper.analysis_status === 'simple_only';
  const isFailed = paper.analysis_status === 'failed';

  // 是否有结果显示（简单或全文）
  const hasResult = isSimpleCompleted || isAnalyzingFulltext || isCompleted || isSimpleOnly;
  // 是否是最终结果
  const isFinalResult = isCompleted || isSimpleOnly;
  // 是否正在分析中
  const isAnalyzing = isPending || isAnalyzingSimple || isAnalyzingFulltext;

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className={`
          group hover:bg-gray-50 transition-colors
          ${isCompleted ? 'bg-green-50/30' : ''}
          ${isSimpleOnly ? 'bg-blue-50/30' : ''}
          ${isAnalyzingFulltext ? 'bg-amber-50/30' : ''}
          ${isAnalyzingSimple || isPending ? 'bg-amber-50/20' : ''}
          ${isFailed ? 'bg-red-50/30' : ''}
        `}
      >
        {/* 展开按钮 */}
        <td className="px-4 py-3">
          <button
            onClick={onToggle}
            className="p-1 rounded hover:bg-gray-200 transition-colors"
          >
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={16} className="text-gray-400" />
            </motion.div>
          </button>
        </td>

        {/* 标题 */}
        <td className="px-4 py-3">
          <a
            href={paper.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-[#94070a] hover:text-[#7a0608] hover:underline line-clamp-2"
            title={paper.title}
          >
            {paper.title}
          </a>
        </td>

        {/* 年份 */}
        <td className="px-4 py-3 text-center">
          <span className="text-sm text-gray-700">{paper.year}</span>
        </td>

        {/* 引用数 */}
        <td className="px-4 py-3 text-center">
          <span className="text-sm font-medium text-gray-700">
            {paper.citation_count.toLocaleString()}
          </span>
        </td>

        {/* 方法 */}
        <td className="px-4 py-3">
          {isPending ? (
            // 状态1: 分析中 - 只显示 spinner
            <Spinner size={16} className="text-amber-500" />
          ) : isAnalyzingSimple ? (
            // 分析简单摘要中
            <Spinner size={16} className="text-amber-500" />
          ) : isFailed ? (
            <span className="inline-flex items-center gap-1 text-red-600 text-sm">
              <span>分析失败</span>
            </span>
          ) : hasResult && paper.analysis?.method && paper.analysis.method !== 'N/A' && !paper.analysis.method.includes('not directly reported') ? (
            // 有方法结果
            <div className="flex items-center gap-1.5">
              <span className={`inline-flex px-2 py-1 rounded-md text-xs font-semibold border ${isFinalResult ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-[#94070a] border-red-100'}`}>
                {paper.analysis.method}
              </span>
              {isAnalyzingFulltext && (
                <Spinner size={12} className="text-amber-500" />
              )}
            </div>
          ) : hasResult ? (
            // 无方法结果但分析完成
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-400">--</span>
              {isAnalyzingFulltext && (
                <Spinner size={12} className="text-amber-500" />
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-sm">--</span>
          )}
        </td>

        {/* 准确率 */}
        <td className="px-4 py-3">
          {isPending || isAnalyzingSimple ? (
            // 状态1: 分析中 - 只显示 spinner
            <Spinner size={16} className="text-amber-500" />
          ) : isFailed ? (
            <span className="text-red-400 text-sm">--</span>
          ) : hasResult && paper.analysis?.accuracy && paper.analysis.accuracy !== 'N/A' && !paper.analysis.accuracy.includes('not directly reported') ? (
            // 有准确率结果
            <div className="flex items-center gap-1.5">
              <span className={`font-semibold text-sm ${isFinalResult ? 'text-green-600' : 'text-blue-600'}`}>
                {paper.analysis.accuracy}%
              </span>
              {isAnalyzingFulltext && (
                <Spinner size={10} className="text-amber-500" />
              )}
            </div>
          ) : hasResult ? (
            // 无准确率结果但分析完成
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 text-sm">--</span>
              {isAnalyzingFulltext && (
                <Spinner size={10} className="text-amber-500" />
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-sm">--</span>
          )}
        </td>

        {/* PDF */}
        <td className="px-4 py-3 text-center">
          {paper.has_pdf && paper.pdf_url ? (
            <a
              href={paper.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="查看PDF"
            >
              <FileText size={18} />
            </a>
          ) : (
            <span className="text-gray-300">
              <FileText size={18} />
            </span>
          )}
        </td>
      </motion.tr>

      {/* 展开详情 */}
      <AnimatePresence>
        {isExpanded && (
          <tr>
            <td colSpan={7} className="p-0">
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="bg-gray-50 border-b border-gray-100 overflow-hidden"
              >
                <div className="px-4 py-4 space-y-4">
                  {/* 论文标题 - 展开后再次强调 */}
                  <div className="border-l-4 border-[#94070a] pl-3">
                    <h4 className="text-sm font-semibold text-gray-900 leading-relaxed">
                      {paper.title}
                    </h4>
                  </div>

                  {/* 摘要 */}
                  <motion.div
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.05 }}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-semibold text-[#94070a]">摘要</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500">被引用 {paper.citation_count.toLocaleString()} 次 · {paper.year}</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed bg-white p-3 rounded-lg border border-gray-100">
                      {paper.abstract}
                    </p>
                  </motion.div>

                  {/* 作者 */}
                  <div className="flex gap-3">
                    <span className="text-xs text-gray-500 w-12 shrink-0 pt-0.5">作者</span>
                    <span className="text-sm text-gray-700">{paper.authors}</span>
                  </div>

                  {/* 创新点 - 分析完成后显示 */}
                  {hasResult && paper.analysis?.innovation && (
                    <motion.div
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className="flex gap-3"
                    >
                      <span className="text-xs text-gray-500 w-12 shrink-0 pt-0.5">创新点</span>
                      <div className="flex-1 flex items-center gap-2">
                        <span className="text-sm text-gray-700">{paper.analysis.innovation}</span>
                        {isAnalyzingFulltext && (
                          <Spinner size={10} className="text-amber-500" />
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* 性能指标 - 分析完成后显示 */}
                  {hasResult && paper.analysis?.metrics && (
                    <motion.div
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.15 }}
                      className="flex gap-3"
                    >
                      <span className="text-xs text-gray-500 w-12 shrink-0 pt-0.5">指标</span>
                      <div className="flex-1 flex items-center gap-2">
                        <span className="text-sm font-medium text-[#94070a]">{paper.analysis.metrics}</span>
                        {isAnalyzingFulltext && (
                          <Spinner size={10} className="text-amber-500" />
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* 论文ID */}
                  <div className="flex gap-3 items-center">
                    <span className="text-xs text-gray-500 w-12 shrink-0">ID</span>
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                      <code className="text-xs text-gray-600 font-mono">{paper.paper_id}</code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(paper.paper_id);
                          // 可以添加一个临时的提示，但这里简化处理
                        }}
                        className="text-gray-400 hover:text-[#94070a] transition-colors"
                        title="复制论文ID"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                      </button>
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex gap-2 pt-2 border-t border-gray-200 mt-3">
                    <a
                      href={paper.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink size={12} />
                      查看论文
                    </a>
                    {paper.has_pdf && paper.pdf_url && (
                      <a
                        href={paper.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#94070a] text-white rounded-lg text-xs hover:bg-[#7a0608] transition-colors"
                      >
                        <FileText size={12} />
                        查看PDF
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
};
